import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

interface BatchJob {
  id: string;
  name: string;
  template: BatchTemplate;
  dataSource: DataSource;
  variations: Variation[];
  status: 'draft' | 'processing' | 'completed' | 'error';
  progress: number;
  totalItems: number;
  completedItems: number;
  outputs: GeneratedOutput[];
  createdAt: Date;
  estimatedTime: number; // minutes
}

interface BatchTemplate {
  id: string;
  name: string;
  type: 'poster' | 'banner' | 'flyer' | 'pop' | 'label';
  baseDesign: string;
  variables: TemplateVariable[];
}

interface TemplateVariable {
  id: string;
  name: string;
  type: 'text' | 'image' | 'number' | 'date' | 'color';
  placeholder: string;
  required: boolean;
  defaultValue?: any;
}

interface DataSource {
  type: 'csv' | 'excel' | 'api' | 'manual';
  data: DataRow[];
  headers: string[];
  mappings: DataMapping[];
}

interface DataRow {
  [key: string]: any;
}

interface DataMapping {
  variableId: string;
  dataColumn: string;
  transformation?: string;
}

interface Variation {
  id: string;
  name: string;
  data: DataRow;
  preview?: string;
  status: 'pending' | 'generating' | 'completed' | 'error';
}

interface GeneratedOutput {
  variationId: string;
  fileName: string;
  format: 'pdf' | 'png' | 'jpg';
  size: number;
  downloadUrl: string;
}

interface BatchGenerationProps {
  onBatchStart?: (job: BatchJob) => void;
  onBatchComplete?: (job: BatchJob) => void;
  selectedTemplate?: BatchTemplate;
}

const BatchGeneration: React.FC<BatchGenerationProps> = ({
  onBatchStart,
  onBatchComplete,
  selectedTemplate
}) => {
  const [batchJobs, setBatchJobs] = useState<BatchJob[]>([]);
  const [activeBatch, setActiveBatch] = useState<BatchJob | null>(null);
  const [step, setStep] = useState<'template' | 'data' | 'mapping' | 'preview' | 'generate'>('template');
  const [selectedTemplateLocal, setSelectedTemplateLocal] = useState<BatchTemplate | null>(selectedTemplate || null);
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [mappings, setMappings] = useState<DataMapping[]>([]);
  const [previewVariations, setPreviewVariations] = useState<Variation[]>([]);

  // サンプルテンプレート
  const sampleTemplates: BatchTemplate[] = [
    {
      id: 'batch-tmpl001',
      name: '商品価格POP',
      type: 'pop',
      baseDesign: '💰',
      variables: [
        { id: 'v1', name: '商品名', type: 'text', placeholder: '商品名を入力', required: true },
        { id: 'v2', name: '価格', type: 'number', placeholder: '0', required: true },
        { id: 'v3', name: '特価', type: 'number', placeholder: '0', required: false },
        { id: 'v4', name: 'キャッチコピー', type: 'text', placeholder: 'お買い得！', required: false }
      ]
    },
    {
      id: 'batch-tmpl002',
      name: 'セール告知バナー',
      type: 'banner',
      baseDesign: '🏷️',
      variables: [
        { id: 'v5', name: 'タイトル', type: 'text', placeholder: 'セール名', required: true },
        { id: 'v6', name: '割引率', type: 'number', placeholder: '0', required: true },
        { id: 'v7', name: '期間', type: 'text', placeholder: '〇月〇日まで', required: true }
      ]
    },
    {
      id: 'batch-tmpl003',
      name: '商品ラベル',
      type: 'label',
      baseDesign: '🏷️',
      variables: [
        { id: 'v8', name: 'SKU', type: 'text', placeholder: 'SKU-000', required: true },
        { id: 'v9', name: '商品名', type: 'text', placeholder: '商品名', required: true },
        { id: 'v10', name: 'バーコード', type: 'text', placeholder: '4901234567890', required: true }
      ]
    }
  ];

  // サンプルデータ生成
  const generateSampleData = (): DataSource => {
    return {
      type: 'csv',
      headers: ['商品名', '通常価格', '特価', 'キャッチコピー', 'カテゴリ'],
      data: [
        { '商品名': '北海道牛乳 1L', '通常価格': 298, '特価': 258, 'キャッチコピー': '期間限定特価！', 'カテゴリ': '乳製品' },
        { '商品名': 'プレミアムヨーグルト', '通常価格': 198, '特価': 168, 'キャッチコピー': '新商品！', 'カテゴリ': '乳製品' },
        { '商品名': 'オーガニック野菜セット', '通常価格': 580, '特価': 480, 'キャッチコピー': '産地直送', 'カテゴリ': '野菜' },
        { '商品名': '特選和牛 100g', '通常価格': 1280, '特価': 980, 'キャッチコピー': '本日のおすすめ', 'カテゴリ': '精肉' },
        { '商品名': '季節のフルーツ盛り合わせ', '通常価格': 680, '特価': 580, 'キャッチコピー': '旬の味覚', 'カテゴリ': 'フルーツ' }
      ],
      mappings: []
    };
  };

  // データアップロード処理
  const handleDataUpload = (file: File) => {
    // ファイル処理シミュレーション
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = generateSampleData();
      setDataSource(data);
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  // マッピング自動提案
  const suggestMappings = () => {
    if (!selectedTemplateLocal || !dataSource) return;

    const suggestions: DataMapping[] = selectedTemplateLocal.variables.map(variable => {
      // 名前の類似性でマッチング
      const matchedColumn = dataSource.headers.find(header => 
        header.toLowerCase().includes(variable.name.toLowerCase()) ||
        variable.name.toLowerCase().includes(header.toLowerCase())
      );

      return {
        variableId: variable.id,
        dataColumn: matchedColumn || dataSource.headers[0],
        transformation: undefined
      };
    });

    setMappings(suggestions);
  };

  // プレビュー生成
  const generatePreviews = () => {
    if (!dataSource || !selectedTemplateLocal) return;

    const variations: Variation[] = dataSource.data.slice(0, 5).map((row, index) => ({
      id: `var-${index}`,
      name: row[dataSource.headers[0]] || `バリエーション${index + 1}`,
      data: row,
      status: 'pending',
      preview: '🖼️'
    }));

    setPreviewVariations(variations);
    setStep('preview');
  };

  // バッチ生成実行
  const executeBatchGeneration = () => {
    if (!selectedTemplateLocal || !dataSource) return;

    const newBatch: BatchJob = {
      id: `batch-${Date.now()}`,
      name: `${selectedTemplateLocal.name} - ${new Date().toLocaleDateString('ja-JP')}`,
      template: selectedTemplateLocal,
      dataSource,
      variations: dataSource.data.map((row, index) => ({
        id: `var-${index}`,
        name: row[dataSource.headers[0]] || `バリエーション${index + 1}`,
        data: row,
        status: 'pending'
      })),
      status: 'processing',
      progress: 0,
      totalItems: dataSource.data.length,
      completedItems: 0,
      outputs: [],
      createdAt: new Date(),
      estimatedTime: Math.ceil(dataSource.data.length * 0.5)
    };

    setActiveBatch(newBatch);
    setBatchJobs([...batchJobs, newBatch]);
    onBatchStart?.(newBatch);
    setStep('generate');

    // 生成プロセスシミュレーション
    let completed = 0;
    const interval = setInterval(() => {
      completed += 1;
      const progress = (completed / newBatch.totalItems) * 100;
      
      const updatedBatch = {
        ...newBatch,
        progress,
        completedItems: completed,
        status: completed === newBatch.totalItems ? 'completed' : 'processing'
      } as BatchJob;

      setActiveBatch(updatedBatch);
      
      if (completed >= newBatch.totalItems) {
        clearInterval(interval);
        onBatchComplete?.(updatedBatch);
      }
    }, 500);
  };

  const getStepIcon = (currentStep: string) => {
    const icons = {
      template: '📄',
      data: '📊',
      mapping: '🔗',
      preview: '👁️',
      generate: '⚙️'
    };
    return icons[currentStep as keyof typeof icons];
  };

  const renderStepContent = () => {
    switch (step) {
      case 'template':
        return (
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              テンプレート選択
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: spacing.md }}>
              {sampleTemplates.map(template => (
                <Card
                  key={template.id}
                  onClick={() => setSelectedTemplateLocal(template)}
                  style={{
                    cursor: 'pointer',
                    border: selectedTemplateLocal?.id === template.id 
                      ? `2px solid ${agentThemes.creative.primary}`
                      : `1px solid ${colors.support.gray}`,
                    transition: 'all 200ms ease'
                  }}
                >
                  <div style={{ padding: spacing.md }}>
                    <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: spacing.sm }}>
                      {template.baseDesign}
                    </div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, color: colors.text.dark, marginBottom: spacing.xs }}>
                      {template.name}
                    </h4>
                    <Badge size="sm" variant="secondary">
                      {template.type}
                    </Badge>
                    <div style={{ marginTop: spacing.sm }}>
                      <div style={{ fontSize: '0.75rem', color: colors.text.light, marginBottom: spacing.xs }}>
                        変数: {template.variables.length}個
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                        {template.variables.map(v => (
                          <Badge key={v.id} size="sm" variant="ghost">
                            {v.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            {selectedTemplateLocal && (
              <Button
                onClick={() => setStep('data')}
                style={{
                  marginTop: spacing.lg,
                  backgroundColor: agentThemes.creative.primary,
                  color: '#FFFFFF'
                }}
              >
                次へ: データ準備
              </Button>
            )}
          </div>
        );

      case 'data':
        return (
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              データソース設定
            </h3>
            <div style={{
              border: `2px dashed ${colors.support.gray}`,
              borderRadius: borderRadius.md,
              padding: spacing.xl,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 200ms ease',
              backgroundColor: dataSource ? colors.background.secondary : colors.background.primary
            }}>
              {dataSource ? (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: spacing.sm }}>✅</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text.dark }}>
                    データ読み込み完了
                  </div>
                  <div style={{ fontSize: '0.875rem', color: colors.text.secondary, marginTop: spacing.sm }}>
                    {dataSource.data.length}件のデータ / {dataSource.headers.length}列
                  </div>
                  <div style={{ marginTop: spacing.md }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${colors.support.gray}` }}>
                          {dataSource.headers.map(header => (
                            <th key={header} style={{
                              padding: spacing.sm,
                              textAlign: 'left',
                              fontWeight: 600,
                              color: colors.text.dark
                            }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dataSource.data.slice(0, 3).map((row, index) => (
                          <tr key={index} style={{ borderBottom: `1px solid ${colors.support.gray}` }}>
                            {dataSource.headers.map(header => (
                              <td key={header} style={{
                                padding: spacing.sm,
                                color: colors.text.secondary
                              }}>
                                {row[header]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {dataSource.data.length > 3 && (
                      <div style={{
                        marginTop: spacing.sm,
                        fontSize: '0.875rem',
                        color: colors.text.light
                      }}>
                        他 {dataSource.data.length - 3}件のデータ
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleDataUpload(e.target.files[0]);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="data-upload"
                  />
                  <label htmlFor="data-upload">
                    <div style={{ fontSize: '2rem', marginBottom: spacing.sm }}>📤</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text.dark }}>
                      データファイルをアップロード
                    </div>
                    <div style={{ fontSize: '0.875rem', color: colors.text.light, marginTop: spacing.sm }}>
                      CSV, Excel形式に対応
                    </div>
                  </label>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.lg }}>
              <Button
                variant="outline"
                onClick={() => setDataSource(generateSampleData())}
              >
                サンプルデータを使用
              </Button>
              {dataSource && (
                <Button
                  onClick={() => {
                    suggestMappings();
                    setStep('mapping');
                  }}
                  style={{
                    backgroundColor: agentThemes.creative.primary,
                    color: '#FFFFFF'
                  }}
                >
                  次へ: マッピング設定
                </Button>
              )}
            </div>
          </div>
        );

      case 'mapping':
        return (
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              データマッピング
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: colors.text.light,
              marginBottom: spacing.lg
            }}>
              テンプレートの変数とデータ列を関連付けてください
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {selectedTemplateLocal?.variables.map(variable => {
                const mapping = mappings.find(m => m.variableId === variable.id);
                return (
                  <Card key={variable.id}>
                    <div style={{ padding: spacing.md }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.dark }}>
                            {variable.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                            {variable.type} {variable.required && '(必須)'}
                          </div>
                        </div>
                        <div style={{ fontSize: '1.5rem', color: colors.text.light }}>→</div>
                        <Select
                          value={mapping?.dataColumn || ''}
                          onChange={(e) => {
                            const newMappings = mappings.filter(m => m.variableId !== variable.id);
                            newMappings.push({
                              variableId: variable.id,
                              dataColumn: e.target.value,
                              transformation: undefined
                            });
                            setMappings(newMappings);
                          }}
                          style={{ width: '200px' }}
                        >
                          <option value="">選択してください</option>
                          {dataSource?.headers.map(header => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            <Button
              onClick={generatePreviews}
              disabled={mappings.length === 0}
              style={{
                marginTop: spacing.lg,
                backgroundColor: agentThemes.creative.primary,
                color: '#FFFFFF'
              }}
            >
              次へ: プレビュー確認
            </Button>
          </div>
        );

      case 'preview':
        return (
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              生成プレビュー
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: colors.text.light,
              marginBottom: spacing.lg
            }}>
              最初の5件のプレビューを確認してください
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: spacing.md }}>
              {previewVariations.map(variation => (
                <Card key={variation.id}>
                  <div style={{ padding: spacing.md }}>
                    <div style={{
                      height: '150px',
                      backgroundColor: colors.background.secondary,
                      borderRadius: borderRadius.sm,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: spacing.sm,
                      fontSize: '3rem'
                    }}>
                      {variation.preview}
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.dark }}>
                      {variation.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                      {Object.keys(variation.data).length}項目
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.md,
              backgroundColor: colors.background.secondary,
              borderRadius: borderRadius.sm
            }}>
              <div style={{ fontSize: '0.875rem', marginBottom: spacing.sm }}>
                <strong>生成概要:</strong>
              </div>
              <div style={{ display: 'flex', gap: spacing.xl, fontSize: '0.875rem', color: colors.text.secondary }}>
                <div>合計アイテム数: {dataSource?.data.length}</div>
                <div>推定時間: {Math.ceil((dataSource?.data.length || 0) * 0.5)}分</div>
                <div>出力形式: PDF, PNG</div>
              </div>
            </div>
            <Button
              onClick={executeBatchGeneration}
              style={{
                marginTop: spacing.lg,
                backgroundColor: agentThemes.creative.primary,
                color: '#FFFFFF'
              }}
            >
              バッチ生成を開始
            </Button>
          </div>
        );

      case 'generate':
        return (
          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: colors.text.dark,
              marginBottom: spacing.md
            }}>
              生成中
            </h3>
            {activeBatch && (
              <Card>
                <div style={{ padding: spacing.lg }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.md }}>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text.dark }}>
                        {activeBatch.name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: colors.text.light }}>
                        {activeBatch.completedItems} / {activeBatch.totalItems} 完了
                      </div>
                    </div>
                    <Badge
                      style={{
                        backgroundColor: activeBatch.status === 'completed' 
                          ? colors.status.success 
                          : activeBatch.status === 'processing'
                          ? colors.accent.blue
                          : colors.status.error,
                        color: '#FFFFFF'
                      }}
                    >
                      {activeBatch.status === 'completed' ? '完了' : 
                       activeBatch.status === 'processing' ? '処理中' : 'エラー'}
                    </Badge>
                  </div>
                  <div style={{
                    height: '8px',
                    backgroundColor: colors.support.gray,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: spacing.md
                  }}>
                    <div style={{
                      width: `${activeBatch.progress}%`,
                      height: '100%',
                      backgroundColor: agentThemes.creative.primary,
                      transition: 'width 500ms ease'
                    }} />
                  </div>
                  <div style={{ fontSize: '0.875rem', color: colors.text.secondary }}>
                    残り時間: 約{Math.ceil((activeBatch.totalItems - activeBatch.completedItems) * 0.5)}分
                  </div>
                  {activeBatch.status === 'completed' && (
                    <Button
                      style={{
                        marginTop: spacing.lg,
                        width: '100%',
                        backgroundColor: colors.status.success,
                        color: '#FFFFFF'
                      }}
                    >
                      すべてダウンロード
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ padding: spacing.lg }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.text.dark,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.sm
        }}>
          <span>{agentThemes.creative.icon}</span>
          バッチ生成
        </h2>
      </div>

      {/* ステップインジケーター */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: spacing.xl,
        padding: spacing.md,
        backgroundColor: colors.background.secondary,
        borderRadius: borderRadius.md
      }}>
        {['template', 'data', 'mapping', 'preview', 'generate'].map((s, index) => {
          const steps = ['template', 'data', 'mapping', 'preview', 'generate'];
          const currentIndex = steps.indexOf(step);
          const stepIndex = steps.indexOf(s);
          const isActive = s === step;
          const isCompleted = stepIndex < currentIndex;

          return (
            <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: isActive 
                    ? agentThemes.creative.primary 
                    : isCompleted 
                    ? colors.status.success 
                    : colors.support.gray,
                  color: isActive || isCompleted ? '#FFFFFF' : colors.text.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '1.25rem'
                }}>
                  {isCompleted ? '✓' : getStepIcon(s)}
                </div>
                <div style={{
                  marginTop: spacing.xs,
                  fontSize: '0.75rem',
                  color: isActive ? colors.text.dark : colors.text.light,
                  fontWeight: isActive ? 600 : 400
                }}>
                  {s === 'template' && 'テンプレート'}
                  {s === 'data' && 'データ'}
                  {s === 'mapping' && 'マッピング'}
                  {s === 'preview' && 'プレビュー'}
                  {s === 'generate' && '生成'}
                </div>
              </div>
              {index < 4 && (
                <div style={{
                  flex: 1,
                  height: '2px',
                  backgroundColor: stepIndex < currentIndex ? colors.status.success : colors.support.gray,
                  margin: '0 -20px'
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* コンテンツエリア */}
      {renderStepContent()}

      {/* 履歴 */}
      {batchJobs.length > 0 && step !== 'generate' && (
        <div style={{ marginTop: spacing.xl }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            color: colors.text.dark,
            marginBottom: spacing.md
          }}>
            生成履歴
          </h3>
          <div style={{ display: 'grid', gap: spacing.sm }}>
            {batchJobs.slice(-3).map(job => (
              <Card key={job.id}>
                <div style={{ padding: spacing.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.dark }}>
                      {job.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                      {job.createdAt.toLocaleString('ja-JP')} | {job.totalItems}件
                    </div>
                  </div>
                  <Badge
                    style={{
                      backgroundColor: job.status === 'completed' ? colors.status.success : colors.accent.blue,
                      color: '#FFFFFF'
                    }}
                  >
                    {job.status === 'completed' ? '完了' : '処理中'}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchGeneration;